import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { TabsModule } from 'primeng/tabs';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';

import { AnagClientiService } from '@/core/service/anag-clienti.service';
import { AnagFornitoriService } from '@/core/service/anag-fornitori.service';
import { UtilType } from '@/core/models/models/dominio/util.model';

@Component({
    selector: 'app-anagrafica',
    templateUrl: './anagrafica.component.html',
    styleUrl: './anagrafica.component.scss',
    providers: [ConfirmationService, MessageService],
    imports: [CommonModule, ConfirmDialogModule, ToastModule, TableModule, InputTextModule, ButtonModule, RippleModule, ToolbarModule, MenuModule, TabsModule, BadgeModule]
})
export class AnagraficaComponent {
    UtilType = UtilType;

    activeTab = signal<'clienti' | 'fornitori'>('clienti');

    clienti: any[] = [];
    fornitori: any[] = [];

    loadingClienti = false;
    loadingFornitori = false;

    expandedClienti: Record<string | number, boolean> = {};
    expandedFornitori: Record<string, boolean> = {};

    readonly countries = [
        { name: 'Italia', code: 'IT' },
        { name: 'Francia', code: 'FR' },
        { name: 'Germania', code: 'DE' },
        { name: 'Spagna', code: 'ES' },
        { name: 'Regno Unito', code: 'GB' },
        { name: 'Stati Uniti', code: 'US' }
    ];

    constructor(
        private anagClientiService: AnagClientiService,
        private anagFornitoriService: AnagFornitoriService,
        private confirmation: ConfirmationService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadClienti();
        this.loadFornitori();
    }

    private loadClienti(): void {
        this.loadingClienti = true;
        this.anagClientiService.list().subscribe({
            next: (res) => (this.clienti = res ?? []),
            error: () => ((this.clienti = []), (this.loadingClienti = false)),
            complete: () => (this.loadingClienti = false)
        });
    }

    private loadFornitori(): void {
        this.loadingFornitori = true;
        this.anagFornitoriService.list().subscribe({
            next: (res) => (this.fornitori = res ?? []),
            error: () => ((this.fornitori = []), (this.loadingFornitori = false)),
            complete: () => (this.loadingFornitori = false)
        });
    }

    onClientiRowExpand(e: { data: any }) {
        const row = e.data;
        if (row?.id != null) this.expandedClienti[String(row.id)] = true;
    }
    onClientiRowCollapse(e: { data: any }) {
        const row = e.data;
        if (row?.id != null) delete this.expandedClienti[String(row.id)];
    }
    onFornitoriRowExpand(e: { data: any }) {
        const row = e?.data;
        if (row?.id != null) this.expandedFornitori[String(row.id)] = true;
    }
    onFornitoriRowCollapse(e: { data: any }) {
        const row = e?.data;
        if (row?.id != null) delete this.expandedFornitori[String(row.id)];
    }

    // ===== Navigazione alle pagine di dettaglio =====
    nuovoCliente() {
        this.router.navigate(['/anagrafica/cliente/nuovo']);
    }
    modificaCliente(row: any) {
        this.router.navigate(['/anagrafica/cliente', row.id]);
    }
    nuovoFornitore() {
        this.router.navigate(['/anagrafica/fornitore/nuovo']);
    }
    modificaFornitore(row: any) {
        this.router.navigate(['/anagrafica/fornitore', row.id]);
    }

    onElimina(tipo: 'cliente' | 'fornitore', row: any) {
        const id = row?.id;
        if (id == null) {
            this.messageService.add({ severity: 'warn', summary: 'ID mancante', detail: 'Impossibile eliminare: ID non presente' });
            return;
        }
        this.confirmation.confirm({
            message: `Sei sicuro di voler eliminare ${tipo === 'cliente' ? 'il cliente' : 'il fornitore'} “${row?.ragioneSociale ?? row?.nome ?? ''}”?`,
            header: 'Conferma eliminazione',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptIcon: 'pi pi-trash',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                const req$ = tipo === 'cliente' ? this.anagClientiService.delete(id) : this.anagFornitoriService.delete(id);
                req$.subscribe({
                    next: () => {
                        if (tipo === 'cliente') this.clienti = this.clienti.filter((c) => c.id !== id);
                        else this.fornitori = this.fornitori.filter((f) => f.id !== id);
                        this.messageService.add({ severity: 'success', summary: 'Eliminato', detail: `${tipo === 'cliente' ? 'Cliente' : 'Fornitore'} eliminato correttamente` });
                    },
                    error: (err) => {
                        console.error('Errore eliminazione', err);
                        this.messageService.add({ severity: 'error', summary: 'Errore', detail: `Non è stato possibile eliminare ${tipo === 'cliente' ? 'il cliente' : 'il fornitore'}` });
                    }
                });
            }
        });
    }

    getFlagUrl(code: string | null | undefined): string {
        const defaultIso = 'it';
        if (code) {
            const iso = code.toLowerCase();
            if (this.countries.some((c) => c.code.toLowerCase() === iso)) return `https://flagcdn.com/w20/${iso}.png`;
        }
        return `https://flagcdn.com/w20/${defaultIso}.png`;
    }

    getCountryName(code: string | null | undefined): string {
        if (!code) return 'Italia';
        const c = this.countries.find((c) => c.code.toLowerCase() === code.toLowerCase());
        return c ? c.name : 'Italia';
    }
}
